import { breakpoints, vars } from '@etg/wings';
import { useDateFormat, useSearch } from '@eti/providers';
import { css } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import { User, CalendarBlank } from '@phosphor-icons/react';
import { useMediaQuery } from 'usehooks-ts';
import { ONE_WAY } from '../../../../constants/tripTypesConstants';

const titleStyles = css`
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  gap: 4px;
  margin: auto 0;

  @media (min-width: ${breakpoints._768}) {
    align-items: center;
    flex-direction: row;
  }
`;

const titleTextStyles = css`
  display: flex;
  line-height: 1.25rem;

  @media (min-width: ${breakpoints._768}) {
    line-height: 1.125rem;
  }
`;

const separatorStyles = css`
  display: inline-block;
  padding: 0 5px;
`;

const detailsStyles = css`
  align-items: center;
  display: flex;
  line-height: 1.25rem;

  @media (min-width: ${breakpoints._768}) {
    line-height: 1.125rem;
  }
`;

const detailsItemStyles = css`
  align-items: center;
  display: flex;

  &:not(:first-child) {
    margin-left: 1rem;
  }

  @media (min-width: ${breakpoints._768}) {
    &:first-child {
      margin-left: 1rem;
    }
  }

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const iconStyles = css`
  fill: ${vars.colors.text.secondary};
  height: 1.25rem;
  margin-inline-end: 0.5rem;
  width: 1.25rem;
`;

const tripRoutesStyles = css`
  display: flex;
  font-weight: 700;
  margin-inline-start: 5px;

  @media (min-width: ${breakpoints._768}) and (max-width: ${breakpoints._970}) {
    margin-inline-start: 0;
  }
`;

const TripDetailsTitle = () => {
  const { currentSearchData } = useSearch();
  const { day, shortWeekday } = useDateFormat();
  const isTabletOrAboveViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const isLaptopOrAboveViewport = useMediaQuery(`(min-width: ${breakpoints._970})`);
  const { date, destination, origin, travelers, tripType } = currentSearchData;
  const numberOfTravelers = travelers.adults + travelers.childAges.length;

  return (
    <div className={titleStyles}>
      <div className={titleTextStyles} data-testid="tripDetails-title-TitleText">
        <span className={tripRoutesStyles}>
          <span>{origin.city}</span>
          <span className={separatorStyles}>–</span>
          <span>{destination.city}</span>
        </span>
      </div>

      <div className={detailsStyles}>
        <div className={detailsItemStyles}>
          <User className={iconStyles} id="adult-traveler" weight="light" />
          {isLaptopOrAboveViewport ? (
            <div data-testid="tripDetails-title-travelers-desktop">{travelers.summary}</div>
          ) : (
            <div data-testid="tripDetails-title-travelers-mobile">{numberOfTravelers}</div>
          )}
        </div>
        <div className={detailsItemStyles}>
          <CalendarBlank className={iconStyles} id="calendar" weight="light" />
          {isTabletOrAboveViewport ? (
            <div data-testid="tripDetails-title-date-desktop">
              <time dateTime={date.departure}>{dateUtil.format(date.departure, shortWeekday)}</time>
              {tripType !== ONE_WAY && (
                <>
                  <span
                    className={separatorStyles}
                    data-testid="tripDetails-title-separator-desktop"
                  >
                    –
                  </span>
                  <time dateTime={date.return}>{dateUtil.format(date.return, shortWeekday)}</time>
                </>
              )}
            </div>
          ) : (
            <div data-testid="tripDetails-title-date-mobile">
              <time dateTime={date.departure}>{dateUtil.format(date.departure, day)}</time>
              {tripType !== ONE_WAY && (
                <>
                  <span
                    className={separatorStyles}
                    data-testid="tripDetails-title-separator-mobile"
                  >
                    –
                  </span>
                  <time dateTime={date.return}>{dateUtil.format(date.return, day)}</time>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripDetailsTitle;
